
import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Edit, Save, X } from 'lucide-react';

interface NotificationPreferencesProps {
  editing: boolean;
  onEditToggle: () => void;
  onSave: () => void;
}

const NotificationPreferences: React.FC<NotificationPreferencesProps> = ({ 
  editing, 
  onEditToggle, 
  onSave 
}) => {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle>Notification Preferences</CardTitle>
          <CardDescription>Manage how you receive notifications and alerts</CardDescription>
        </div>
        <div className="flex space-x-2">
          {editing ? (
            <>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onSave}
                className="flex items-center gap-1"
              >
                <Save className="h-4 w-4" />
                Save
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={onEditToggle}
                className="flex items-center gap-1"
              >
                <X className="h-4 w-4" />
                Cancel
              </Button>
            </>
          ) : (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onEditToggle}
              className="flex items-center gap-1"
            >
              <Edit className="h-4 w-4" />
              Edit
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Email Notifications</h3>
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Checkbox id="email-alerts" defaultChecked disabled={!editing} />
              <label
                htmlFor="email-alerts"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                System Alerts
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="email-reports" defaultChecked disabled={!editing} />
              <label
                htmlFor="email-reports"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Weekly Reports
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="email-maintenance" defaultChecked disabled={!editing} />
              <label
                htmlFor="email-maintenance"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Maintenance Alerts
              </label>
            </div>
          </div>

          <h3 className="text-lg font-medium pt-4">SMS Notifications</h3>
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Checkbox id="sms-critical" defaultChecked disabled={!editing} />
              <label
                htmlFor="sms-critical"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Critical Alerts Only
              </label>
            </div>
            <div className="space-y-2 pt-2">
              <Label htmlFor="phone-number">Phone Number for SMS</Label>
              <Input id="phone-number" defaultValue="+1 (555) 123-4567" readOnly={!editing} />
            </div>
          </div>

          <h3 className="text-lg font-medium pt-4">SMS Gateway Configuration</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sms-provider">SMS Provider</Label>
                <Input id="sms-provider" defaultValue="Twilio" readOnly={!editing} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sms-api-key">API Key</Label>
                <Input id="sms-api-key" type="password" defaultValue="••••••••••••••••" readOnly={!editing} />
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default NotificationPreferences;
